import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  IconButton,
  AppBar,
  Toolbar,
  Avatar,
  Fade,
  Grid,
  Tabs,
  Tab,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack,
  Insights,
  Dashboard,
  BarChart,
  Public,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import axios from '../utils/axios';
import PortfolioOverview from './PortfolioOverview';
import CurrentPositions from './CurrentPositions';
import TransactionHistory from './TransactionHistory';
import DailyActivity from './DailyActivity';
import Statistics from './statistics/Statistics';
import Settings from './settings/Settings';
import MarketStatusChip from '../design/MarketStatusChip';
import { appBarGradientColors } from '../design/theme';

interface TraderListItem {
  Id: number;
  Name: string;
  Strategy: string;
}

const MainDashboard: React.FC = () => {
  const { traderId, tab } = useParams<{ traderId: string; tab?: string }>();
  const navigate = useNavigate();
  const { logout } = useAuth();
  
  const [traders, setTraders] = useState<TraderListItem[]>([]);
  const [traderData, setTraderData] = useState<{
    name: string;
    strategy: string;
  } | null>(null);
  
  // Map tab names to indices
  const getTabIndex = (tabName?: string): number => {
    switch (tabName) {
      case 'statistics': return 1;
      case 'settings': return 2;
      default: return 0; // overview
    }
  };
  
  const getTabName = (index: number): string => {
    switch (index) {
      case 1: return 'statistics';
      case 2: return 'settings';
      default: return 'overview';
    }
  };
  
  const [activeTab, setActiveTab] = useState(getTabIndex(tab));

  // Fetch trader data on mount
  useEffect(() => {
    const fetchTraderData = async () => {
      try {
        const response = await axios.get('/traders');
        setTraders(response.data);
        const trader = response.data.find((t: any) => t.Id === parseInt(traderId || '0'));
        if (trader) {
          setTraderData({
            name: trader.Name,
            strategy: trader.Strategy
          });
        }
      } catch (error) {
        console.error('Error fetching trader data:', error);
      }
    };
    
    if (traderId) {
      fetchTraderData();
    }
  }, [traderId]);

  // Update active tab when URL changes
  useEffect(() => {
    setActiveTab(getTabIndex(tab));
  }, [tab]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const tabName = getTabName(newValue);
    const newPath = tabName === 'overview' 
      ? `/trader/${traderId}` 
      : `/trader/${traderId}/${tabName}`;
    navigate(newPath);
  };

  const handleBackToSelection = () => {
    navigate('/');
  };

  const handleTraderChange = (newId: number) => {
    navigate(`/trader/${newId}${tab ? `/${tab}` : ''}`);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getGradientColors = (strategy: string) => {
    switch (strategy.toLowerCase()) {
      case 'strategy_1':
        return '#2196F3, #21CBF3';
      case 'strategy_2':
        return '#9C27B0, #E91E63';
      default:
        return appBarGradientColors;
    }
  };

  // Show loading if trader data is not loaded yet
  if (!traderData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        Loading...
      </Box>
    );
  }

  const traderStrategy = traderData.strategy;

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Header */}
      <AppBar 
        position="static" 
        elevation={0}
        sx={{
          background: `linear-gradient(135deg, ${getGradientColors(traderStrategy)})`,
          borderRadius: 0,
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleBackToSelection}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          
          <IconButton onClick={() => navigate('/')} aria-label="Home" sx={{ p: 0, mr: 2 }}>
            <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }}>
              <Insights />
            </Avatar>
          </IconButton>
          
          <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden' }}>
            <Select
              value={traderId ? Number(traderId) : ''}
              onChange={(e) => handleTraderChange(Number(e.target.value))}
              variant="standard"
              disableUnderline
              aria-label="Select trader"
              sx={{
                color: 'inherit',
                fontSize: '1.25rem',
                fontWeight: 'bold',
                maxWidth: '100%',
                '& .MuiSelect-select': { py: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                '& .MuiSelect-icon': { color: 'inherit' },
              }}
            >
              {traders.map((t) => (
                <MenuItem key={t.Id} value={t.Id}>
                  {t.Name}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <IconButton color="inherit" onClick={() => navigate('/universe')} aria-label="Stock universe" sx={{ mr: 1 }}>
            <Public />
          </IconButton>

          <Box sx={{ mr: 1 }}>
            <MarketStatusChip />
          </Box>

          <IconButton
            color="inherit"
            onClick={handleLogout}
            aria-label="Logout"
            title="Logout"
          >
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Tab Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Container maxWidth="xl">
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTabs-indicator': {
                background: `linear-gradient(135deg, ${getGradientColors(traderStrategy)})`,
                height: 3,
              },
              '& .MuiTabs-scrollButtons': {
                '&.Mui-disabled': { opacity: 0.3 },
              },
            }}
          >
            <Tab 
              icon={<Dashboard />} 
              label="Overview" 
              sx={{
                textTransform: 'none',
                fontWeight: 'bold',
                '&.Mui-selected': {
                  background: `linear-gradient(135deg, ${getGradientColors(traderStrategy)})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                },
              }}
            />
            <Tab 
              icon={<BarChart />} 
              label="Statistics" 
              sx={{
                textTransform: 'none',
                fontWeight: 'bold',
                '&.Mui-selected': {
                  background: `linear-gradient(135deg, ${getGradientColors(traderStrategy)})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                },
              }}
            />
            <Tab 
              icon={<SettingsIcon />} 
              label="Settings" 
              sx={{
                textTransform: 'none',
                fontWeight: 'bold',
                '&.Mui-selected': {
                  background: `linear-gradient(135deg, ${getGradientColors(traderStrategy)})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                },
              }}
            />
          </Tabs>
        </Container>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
          <Fade in timeout={800}>
            <Grid container spacing={4}>
              {/* Portfolio Overview */}
              <Grid item xs={12}>
                <Paper
                  elevation={2}
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    backgroundColor: '#d4d9e6',
                  }}
                >
                  <Box sx={{ p: 3 }}>
                    <PortfolioOverview traderId={parseInt(traderId || '0')} />
                  </Box>
                </Paper>
              </Grid>

              {/* Current Positions */}
              <Grid item xs={12}>
                <Paper
                  elevation={2}
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ p: 3 }}>
                    <CurrentPositions traderId={parseInt(traderId || '0')} />
                  </Box>
                </Paper>
              </Grid>

              {/* Transaction History */}
              <Grid item xs={12} lg={8}>
                <Paper
                  elevation={2}
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    height: '100%',
                  }}
                >
                  <Box sx={{ p: 3 }}>
                    <TransactionHistory traderId={parseInt(traderId || '0')} />
                  </Box>
                </Paper>
              </Grid>

              {/* Daily Activity */}
              <Grid item xs={12} lg={4}>
                <Paper
                  elevation={2}
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    height: '100%',
                  }}
                >
                  <Box sx={{ p: 3 }}>
                    <DailyActivity traderId={parseInt(traderId || '0')} />
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Fade>
        </Container>
      )}

      {activeTab === 1 && (
        <Statistics traderId={parseInt(traderId || '0')} />
      )}

      {activeTab === 2 && (
        <Settings traderId={parseInt(traderId || '0')} />
      )}
    </Box>
  );
};

export default MainDashboard; 